<template>
  <div>
    <ButtonDefault
      v-if="renderTrigger && label"
      color="red"
      @click="abrir"
    >
      <XMarkIcon class="size-4" />
      {{ label }}
    </ButtonDefault>
    <ButtonGroup
      v-else-if="renderTrigger"
      :show-label="false"
      :buttons="buttonActions"
    />

    <RegistrarMotivoModal
      v-if="!tieneFormulario"
      :id="modalMotivoId"
      title-component="Cerrar alerta"
      warning-message="Al cerrar la alerta se registrará el motivo de cierre."
      confirmation-title="¿Confirmas cerrar esta alerta?"
      :callback-fnc="handleCierreMotivo"
    />

    <ModalCierreFormulario
      v-if="tieneFormulario"
      ref="formularioModal"
      :aler-id="props.alerta.alerId!"
      :codo-id="props.alerta.codoId!"
      :citt-id="cittIdParaCierre"
      @closed="handleFormularioCerrado"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, ref } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { openModal } from '@/modules/Core/usings/generales'
import ButtonGroup from '@/modules/Core/components/general/ButtonGroup.vue'
import ButtonDefault from '@/modules/Core/components/general/ButtonDefault.vue'
import RegistrarMotivoModal from '@/modules/Core/components/general/RegistrarMotivoModal.vue'
import ModalCierreFormulario from './ModalCierreFormulario.vue'
import { alertasService, motivoService } from '@/services'
import type { IAlertAlertas } from '@/modules/Asistencial/interfaces'
import type { IButton } from '@/modules/Core/interfaces'

const props = withDefaults(defineProps<{
  alerta: IAlertAlertas
  onClosed?: () => void
  cmotTipo?: string
  label?: string
  cittIdContext?: number
  actions?: IButton<unknown>[]
  renderTrigger?: boolean
}>(), {
  cmotTipo: 'CIERRE ALERTA',
  onClosed: () => {},
  label: '',
  cittIdContext: undefined,
  actions: () => [],
  renderTrigger: true
})

const emit = defineEmits<{
  closed: []
}>()

const tieneFormulario = computed(() => !!props.alerta.codoId)
const cittIdParaCierre = computed(() => props.cittIdContext || props.alerta.cittId || 0)
const closeAction = computed(() => ({
  label: props.label || (tieneFormulario.value ? 'Cerrar alerta con formulario' : 'Cerrar alerta'),
  click: () => abrir(),
  icon: 'XMarkIcon',
  class: 'size-8 rounded-full text-gray-500 hover:bg-red-100 hover:text-red-800 focus:ring-2 focus:ring-red-500 dark:hover:bg-red-900 dark:hover:text-red-200',
  show: () => true,
  description: '',
  onlyTooltip: false,
  color: 'red',
  expanded: () => false
}))
const buttonActions = computed(() => [
  ...props.actions.slice(0, 1),
  closeAction.value,
  ...props.actions.slice(1)
])

const uid = getCurrentInstance()?.uid ?? Math.floor(Math.random() * 1e9)
const modalMotivoId = `modal-cierre-alerta-${props.alerta.alerId}-${uid}`
const formularioModal = ref<{ cargarFormulario: () => Promise<void>; modalId: string } | null>(null)

const abrir = async () => {
  if (tieneFormulario.value) {
    window.notificacion?.loading().circle('Cargando formulario...')
    try {
      await formularioModal.value?.cargarFormulario()
    } finally {
      window.notificacion?.loading().remove()
    }
    // Abrir el modal específico de ESTA instancia (no colisiona con otras montadas a la vez)
    const id = formularioModal.value?.modalId
    if (id) openModal(id)
  } else {
    openModal(modalMotivoId)
  }
}

const handleCierreMotivo = async (motivo: string) => {
  try {
    window.notificacion.loading().circle()
    await motivoService.create({
      cmotTabla: 'tbl_aler_alertas',
      cmotReferenciaId: props.alerta.alerId,
      cmotMotivo: motivo,
      cmotTipo: props.cmotTipo
    })
    await alertasService.update(props.alerta.alerId!, { alerEstado: 0 })
    window.notificacion.notificacion('success', 'Alerta cerrada correctamente')
    props.onClosed?.()
    emit('closed')
    return true
  } catch {
    window.notificacion.notificacion('failure', 'Error al cerrar la alerta')
    return false
  } finally {
    window.notificacion.loading().remove()
  }
}

const handleFormularioCerrado = () => {
  props.onClosed?.()
  emit('closed')
}

// Permite disparar el flujo de cierre desde un contenedor externo, reutilizando esta misma lógica y sus modales.
defineExpose({ abrir })
</script>
